// Hook de notifications navigateur HTML5 pour VoaChat
// Phase 1 : fonctionne quand l'onglet est ouvert (même en arrière-plan)
// Phase 2 future : Service Worker + Web Push pour onglet fermé
import { useEffect, useRef } from "react";

const STORAGE_KEY_PERM_ASKED = "voachat-notif-asked";
const STORAGE_KEY_ENABLED    = "voachat-notif-enabled";

// Joue un son de notif léger (pas de fichier externe, sinon page accueil 404)
const playNotifSound = () => {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(1320, ctx.currentTime + 0.08);
    gain.gain.setValueAtTime(0.001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
    osc.start(); osc.stop(ctx.currentTime + 0.3);
  } catch { /* AudioContext bloqué (Safari iOS) — silent */ }
};

export const useDmNotifications = (unreadCount, authed) => {
  const previousCount = useRef(unreadCount);
  const originalTitle = useRef(typeof document !== "undefined" ? document.title : "VoaChat");

  // Demande la permission Notification au 1er rendu authentifié
  useEffect(() => {
    if (!authed || typeof window === "undefined" || !("Notification" in window)) return;
    const enabled = localStorage.getItem(STORAGE_KEY_ENABLED) === "1";
    const asked = localStorage.getItem(STORAGE_KEY_PERM_ASKED) === "1";
    if (enabled && Notification.permission === "default" && !asked) {
      localStorage.setItem(STORAGE_KEY_PERM_ASKED, "1");
      Notification.requestPermission().catch(() => {});
    }
  }, [authed]);

  // Détecte une augmentation du compteur non-lus → notif + son + badge titre
  useEffect(() => {
    if (typeof window === "undefined") return;
    const enabled = localStorage.getItem(STORAGE_KEY_ENABLED) === "1";
    const prev = previousCount.current;
    previousCount.current = unreadCount;

    // Badge dans le titre (toujours actif)
    document.title = unreadCount > 0
      ? `(${unreadCount}) ${originalTitle.current}`
      : originalTitle.current;

    if (!enabled) return;
    if (unreadCount <= prev) return; // pas d'augmentation
    if (!("Notification" in window) || Notification.permission !== "granted") return;
    if (!document.hidden && document.hasFocus()) return; // utilisateur déjà sur le chat

    try {
      const n = new Notification("💬 Nouveau message VoaChat 🇲🇬", {
        body: unreadCount === 1
          ? "Tu as 1 nouveau message privé"
          : `Tu as ${unreadCount} nouveaux messages privés`,
        icon: "/icon-192.png",
        badge: "/icon-192.png",
        tag: "voachat-dm",
        renotify: true,
      });
      n.onclick = () => {
        window.focus();
        window.location.href = "/messages";
        n.close();
      };
      playNotifSound();
    } catch { /* iOS Safari et certains contextes refusent — silent */ }
  }, [unreadCount]);
};

// Toggle d'activation pour le toaster réglages
export const setNotificationsEnabled = (on) => {
  localStorage.setItem(STORAGE_KEY_ENABLED, on ? "1" : "0");
  if (on && "Notification" in window && Notification.permission === "default") {
    return Notification.requestPermission();
  }
  return Promise.resolve(Notification.permission);
};

export const getNotificationsEnabled = () => {
  if (typeof window === "undefined" || !("Notification" in window)) return false;
  return localStorage.getItem(STORAGE_KEY_ENABLED) === "1" && Notification.permission === "granted";
};
